import { FormEvent, ReactNode, useEffect, useRef, useState } from "react"
import { FiArrowLeft, FiBriefcase, FiTag, FiBookOpen, FiMessageSquare, FiPaperclip, FiCheck, FiCreditCard, FiRefreshCw, FiSave, FiHash, FiPlus, FiShield, FiTrash2, FiUsers, FiX } from "react-icons/fi"
import AppLayout from "../layout/AppLayout"
import ProgressBarV2 from "../components/ui/ProgressBar_v2"
import SectionHeader from "../components/ui/SectionHeader"
import FileUploadSlot from "../components/patent/FileUploadSlot"
import PdfPreview from "../components/ui/PdfPreview"
import FeesBoxV2 from "../components/trademark/sidebar/FeesBox_v2"
import CurrencySelector from "../components/layout/CurrencySelector"
import AddApplicantBox from "../components/trademark/applicant/AddApplicantBox"

type EarlierRight = { id: number; number: string; name: string; office: "EUIPO" }
type Attachment = { id: number; file: File; category: "arguments" | "evidence" }

// Plain-English descriptions of Article 8 of Regulation (EU) 2017/1001.
const grounds = [
  ["Article 8, paragraph 1, point (a)", "The challenged trademark and the earlier trademark are identical, and cover identical goods or services."],
  ["Article 8, paragraph 1, point (b)", "The marks and their goods or services are identical or similar, creating a likelihood of public confusion, including association with the earlier trademark."],
  ["Article 8, paragraph 5", "The earlier trademark has a reputation. Use of the challenged mark without due cause would unfairly exploit or harm its distinctive character or reputation."],
  ["Article 8, paragraph 3", "An agent or representative applied to register the proprietor's trademark in their own name without consent and cannot justify doing so."],
  ["Article 8, paragraph 4", "An earlier unregistered trademark or other sign used in trade beyond a merely local area gives its holder the right to prohibit use of a later trademark under the applicable law."],
  ["Article 8, paragraph 6", "An earlier designation of origin or geographical indication gives an authorised person the right to prohibit a later trademark under the applicable law, subject to the relevant protection requirements."]
] as const

const inputClass = "mt-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:border-dantzBlue focus:outline-none focus:ring-1 focus:ring-dantzBlue"
const secondaryButton = "inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-medium text-dantzBlue hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-dantzBlue"
const sections = [
  ["reference", "Your reference"],
  ["opposing-party", "Opposing party"],
  ["trademark", "Trademark being opposed"],
  ["rights", "Earlier rights"],
  ["grounds", "Reasons for opposition"],
  ["arguments", "Arguments"],
  ["documents", "Supporting documents"]
]

const progressSections = [
  { target: "reference", label: "Your reference", short: "Reference", icon: <FiHash /> },
  { target: "opposing-party", label: "Opposing party", short: "Opposing party", icon: <FiUsers /> },
  { target: "trademark", label: "Trademark being opposed", short: "Trademark", icon: <FiTag /> },
  { target: "rights", label: "Earlier rights", short: "Earlier rights", icon: <FiShield /> },
  { target: "grounds", label: "Reasons for opposition", short: "Reasons", icon: <FiBookOpen /> },
  { target: "arguments", label: "Arguments", short: "Arguments", icon: <FiMessageSquare /> },
  { target: "documents", label: "Supporting documents", short: "Attachments", icon: <FiPaperclip /> }
]

export default function OppositionPage() {
  const [draftMessage, setDraftMessage] = useState("")
  const [formKey, setFormKey] = useState(0)
  const [activeSection, setActiveSection] = useState("reference")
  const nextId = useRef(2)
  const [reference, setReference] = useState("")
  const [opposingParties, setOpposingParties] = useState<any[]>([])
  const [partyError, setPartyError] = useState("")
  const [trademark, setTrademark] = useState({ number: "", name: "" })
  const [rights, setRights] = useState<EarlierRight[]>([{ id: 1, number: "", name: "", office: "EUIPO" }])
  const [selectedGrounds, setSelectedGrounds] = useState<string[]>([])
  const [argumentsText, setArgumentsText] = useState("")
  const [attachments, setAttachments] = useState<Attachment[]>([])
  const [review, setReview] = useState(false)
  const [error, setError] = useState("")
  const reviewTop = useRef<HTMLDivElement>(null)

  const complete: Record<string, boolean> = {
    reference: Boolean(reference.trim()),
    "opposing-party": opposingParties.length > 0,
    trademark: Boolean(trademark.number.trim()),
    rights: rights.length > 0 && rights.every(right => right.name.trim() && right.office.trim()),
    grounds: selectedGrounds.length > 0,
    arguments: Boolean(argumentsText.trim()) || attachments.some(item => item.category === "arguments"),
    documents: attachments.some(item => item.category === "evidence")
  }

  useEffect(() => {
    if (review || typeof IntersectionObserver === "undefined") return
    const observer = new IntersectionObserver(entries => {
      const visible = entries.filter(entry => entry.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
      if (visible.length) setActiveSection(visible[0].target.id)
    }, { rootMargin: "-10% 0px -65% 0px", threshold: 0 })
    sections.forEach(([id]) => {
      const element = document.getElementById(id)
      if (element) observer.observe(element)
    })
    return () => observer.disconnect()
  }, [review, formKey])

  useEffect(() => {
    if (review) reviewTop.current?.scrollIntoView({ behavior: "smooth", block: "start" })
  }, [review])

  function resetForm() {
    setActiveSection("reference")
    setReference("")
    setOpposingParties([])
    setTrademark({ number: "", name: "" })
    setRights([{ id: 1, number: "", name: "", office: "EUIPO" }])
    setSelectedGrounds([])
    setArgumentsText("")
    setAttachments([])
    setReview(false)
    setError("")
    setPartyError("")
    setFormKey(current => current + 1)
    nextId.current = 2
    setDraftMessage("Form reset.")
  }

  function updateRight(id: number, field: "number" | "name", value: string) {
    setRights(current => current.map(right => right.id === id ? { ...right, [field]: value } : right))
  }

  function addFiles(files: FileList | File[] | null, category: Attachment["category"]) {
    if (!files) return
    const added = Array.from(files).map(file => ({ id: nextId.current++, file, category }))
    setAttachments(current => [...current, ...added])
  }

  function showReview(event: FormEvent) {
    event.preventDefault()
    if (!opposingParties.length) {
      setPartyError("Add an opposing party using the person search or create a new person.")
      document.getElementById("opposing-party")?.scrollIntoView({ behavior: "smooth", block: "start" })
      return
    }
    if (!selectedGrounds.length) {
      setError("Select at least one ground for opposition.")
      document.getElementById("grounds")?.scrollIntoView({ behavior: "smooth", block: "start" })
      return
    }
    setError("")
    setReview(true)
  }

  const representative = (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm">
      <div className="flex items-center justify-between gap-3">
        <p className="font-semibold text-dantzBlue">Dantz IP &amp; Law</p>
      </div>
      <p className="mt-1 text-gray-600">Kurfürstendamm 43, 10719 Berlin, Germany</p>
    </div>
  )

  return (
    <AppLayout title="Trademark Opposition" wide>
      <div>
        <p className="mb-6 text-sm text-gray-600">Prepare an opposition against a trademark application.</p>
        {!review && (
          <div className="sticky top-0 z-30 mb-4 border-b border-gray-200 bg-white">
            <div className="mx-auto max-w-7xl px-6 py-4">
              <section className="sticky top-0 z-50 border-b border-black bg-white pb-6">
                <nav aria-label="Form sections">
                  <ProgressBarV2 complete={complete} sections={progressSections} activeSection={activeSection} onNavigate={setActiveSection} />
                </nav>
              </section>
            </div>
          </div>
        )}
        <div className="flex justify-center py-12 pb-28">
          <div className="grid w-full max-w-7xl grid-cols-1 items-start gap-7 xl:grid-cols-[minmax(0,1fr)_18rem]">
            {review ? (
              <div ref={reviewTop} className="scroll-mt-6 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm sm:p-8">
              <SectionHeader icon={<FiCheck />} title="Review opposition" subtitle="Check your information before returning to the form." />
              <div className="space-y-6 text-sm">
                <ReviewItem label="Your reference" value={reference || "Not provided"} />
                <div><h3 className="mb-2 font-semibold">Opposing party</h3>{opposingParties.map((party, index) => <p key={index} className="mb-3 whitespace-pre-wrap">{[party.type === "legal" ? party.legalName : [party.firstName, party.lastName].filter(Boolean).join(" "), party.euipoPersonIdentifier ? `EUIPO person ID: ${party.euipoPersonIdentifier}` : "", [party.address?.street, party.address?.postalCode, party.address?.city, party.address?.country].filter(Boolean).join(", "), party.contact?.email].filter(Boolean).join("\n")}</p>)}</div>
                {representative}
                <ReviewItem label="Trademark being opposed (EUIPO)" value={[trademark.number, trademark.name].filter(Boolean).join(" - ")} />
                <div><h3 className="mb-2 font-semibold">Earlier rights</h3>{rights.map((right, index) => <p key={right.id} className="mb-2">{index + 1}. {[right.number, right.name, right.office].filter(Boolean).join(" - ")}</p>)}</div>
                <div><h3 className="mb-2 font-semibold">Reasons for opposition</h3><ul className="list-disc space-y-2 pl-5">{grounds.filter(([article]) => selectedGrounds.includes(article)).map(([article, label]) => <li key={article}>{article} - {label}</li>)}</ul></div>
                <ReviewItem label="Arguments" value={argumentsText || (attachments.some(item => item.category === "arguments") ? "Provided in attachments" : "Not provided")} />
                <div><h3 className="mb-2 font-semibold">Argument documents</h3><AttachmentList items={attachments.filter(item => item.category === "arguments")} /></div>
                <div><h3 className="mb-2 font-semibold">Supporting documents</h3><AttachmentList items={attachments.filter(item => item.category === "evidence")} /></div>
                <button type="button" className={secondaryButton} onClick={() => setReview(false)}><FiArrowLeft /> Back to form</button>
              </div>
            </div>
            ) : (
              <form
              key={formKey}
              id="opposition-form"
              onSubmit={showReview}
              onInvalidCapture={event => {
                if (event.target instanceof HTMLElement) {
                  window.setTimeout(() => event.target instanceof HTMLElement && event.target.scrollIntoView({ behavior: "smooth", block: "center" }), 0)
                }
              }}
              className="rounded-2xl border border-gray-200 bg-white shadow-sm"
            >
              <fieldset className="min-w-0 divide-y divide-gray-200">
              <FormSection id="reference" title="Your reference" icon={<FiHash />}>
                <Field label="Reference *"><input required value={reference} onChange={event => setReference(event.target.value)} className={inputClass} /></Field>
              </FormSection>
              <FormSection id="opposing-party" title="Opposing party" icon={<FiUsers />} subtitle="Search EUIPO persons or add a natural person or legal entity.">
                <AddApplicantBox
                  orderingParty={null}
                  initialData={opposingParties}
                  personLabel="Opposing party"
                  searchPlaceholder="Search persons in EUIPO"
                  onChange={parties => { setOpposingParties(parties); setPartyError("") }}
                />
                {partyError && <p role="alert" className="mt-3 text-sm text-red-600">{partyError}</p>}
              </FormSection>
              <FormSection id="representative" title="Representative" icon={<FiBriefcase />}>{representative}</FormSection>
              <FormSection id="trademark" title="Trademark being opposed" icon={<FiTag />} subtitle="Enter the EUIPO application you wish to oppose.">
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Application number *"><input required value={trademark.number} onChange={event => setTrademark({ ...trademark, number: event.target.value })} className={inputClass} /></Field>
                  <Field label="Trademark name (optional)"><input value={trademark.name} onChange={event => setTrademark({ ...trademark, name: event.target.value })} className={inputClass} /></Field>
                </div>
              </FormSection>
              <FormSection id="rights" title="Earlier trademarks and rights" icon={<FiShield />} subtitle="Add each earlier trademark or other right you rely upon.">
                <div className="space-y-4">{rights.map((right, index) => (
                  <fieldset key={right.id} className="rounded-lg border border-gray-200 p-4">
                    <legend className="px-1 text-sm font-semibold text-gray-800">Earlier right {index + 1}</legend>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <Field label="Application / registration number (if available)"><input value={right.number} onChange={event => updateRight(right.id, "number", event.target.value)} className={inputClass} /></Field>
                      <Field label="Trademark name / description of right *"><input required value={right.name} onChange={event => updateRight(right.id, "name", event.target.value)} className={inputClass} /></Field>
                      <Field label="Intellectual property office"><input readOnly value="EUIPO" className={`${inputClass} bg-gray-50 text-gray-500`} /></Field>
                    </div>
                    {rights.length > 1 && <button type="button" aria-label={`Remove earlier right ${index + 1}`} className="mt-4 inline-flex items-center gap-2 text-sm text-red-600" onClick={() => setRights(current => current.filter(item => item.id !== right.id))}><FiTrash2 /> Remove</button>}
                  </fieldset>
                ))}</div>
                <button type="button" className={`${secondaryButton} mt-4`} onClick={() => setRights(current => [...current, { id: nextId.current++, number: "", name: "", office: "EUIPO" }])}><FiPlus /> Add another earlier trademark or right</button>
              </FormSection>
              <FormSection id="grounds" title="Reasons for opposition" icon={<FiBookOpen />} subtitle="Select the reasons you wish to rely on.">
                <fieldset className="space-y-3"><legend className="sr-only">Reasons for opposition</legend>{grounds.map(([article, label]) => (
                  <label key={article} className="flex cursor-pointer items-start gap-3 rounded-lg border border-gray-200 p-3 text-sm hover:bg-gray-50">
                    <input type="checkbox" className="mt-1 h-4 w-4 shrink-0 accent-dantzBlue" checked={selectedGrounds.includes(article)} onChange={event => { setSelectedGrounds(current => event.target.checked ? [...current, article] : current.filter(value => value !== article)); setError("") }} />
                    <span><span className="font-semibold">{article}</span><span className="mt-1 block text-gray-600">{label}</span></span>
                  </label>
                ))}</fieldset>
                {error && <p role="alert" className="mt-3 text-sm text-red-600">{error}</p>}
              </FormSection>
              <FormSection id="arguments" title="Arguments" icon={<FiMessageSquare />} subtitle="Paste your prepared arguments or upload an argument document.">
                <Field label="Arguments supporting the opposition"><textarea rows={8} value={argumentsText} onChange={event => setArgumentsText(event.target.value)} className={inputClass} /></Field>
                <div className="mt-5"><AttachmentUpload category="arguments" items={attachments.filter(item => item.category === "arguments")} onAdd={addFiles} onRemove={id => setAttachments(current => current.filter(item => item.id !== id))} /></div>
              </FormSection>
              <FormSection id="documents" title="Supporting documents" icon={<FiPaperclip />} subtitle="Add evidence and other attachments supporting your opposition.">
                <AttachmentUpload category="evidence" items={attachments.filter(item => item.category === "evidence")} onAdd={addFiles} onRemove={id => setAttachments(current => current.filter(item => item.id !== id))} />
              </FormSection>
              <div className="flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-end sm:p-8">
                <button type="submit" className="rounded-lg bg-dantzBlue px-5 py-3 text-sm font-medium text-white hover:bg-blue-800">Review opposition</button>
              </div>
                </fieldset>
              </form>
            )}
            <aside className="block">
              <div className="flex w-full flex-col gap-4 xl:sticky xl:top-[128px] xl:max-h-[calc(100vh-9rem)] xl:w-72 xl:overflow-y-auto xl:[scrollbar-width:none] xl:[&::-webkit-scrollbar]:hidden">
                <div className="flex flex-col gap-2 rounded-md border border-gray-200 bg-white p-3 text-sm shadow-sm">
                  <button type="button" onClick={() => setDraftMessage("Saving drafts is not available yet.")} className="flex w-full items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-dantzBlue transition-colors hover:border-dantzBlue hover:bg-blue-50">
                    <FiSave className="h-4 w-4" aria-hidden="true" /> Save as draft
                  </button>
                  <button type="button" onClick={resetForm} className="flex w-full items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-dantzBlue transition-colors hover:border-dantzBlue hover:bg-blue-50">
                    <FiRefreshCw className="h-4 w-4" aria-hidden="true" /> Reset form
                  </button>
                  <button type="button" onClick={() => setDraftMessage("Payment is not available in this demo.")} className="flex w-full items-center justify-center gap-2 rounded-lg bg-dantzOrange px-3 py-2 text-sm font-medium text-white transition-colors hover:brightness-110">
                    <FiCreditCard className="h-4 w-4" aria-hidden="true" /> Proceed to payment
                  </button>
                  {draftMessage && <p role="status" className="text-xs text-dantzBlue">{draftMessage}</p>}
                </div>
                <FeesBoxV2 total={480} serviceLines={[{ label: "Opposition filing service", amount: 160 }]} officialLines={[{ label: "EUIPO opposition fee", amount: 320 }]} />
                <div className="rounded-xl border border-gray-200 bg-white p-4">
                  <CurrencySelector />
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </AppLayout>
  )
}

function FormSection({ id, title, icon, subtitle, children }: { id: string; title: string; icon: ReactNode; subtitle?: string; children: ReactNode }) {
  return <section id={id} className="scroll-mt-32 p-5 sm:p-8 xl:p-12"><SectionHeader title={title} icon={icon} subtitle={subtitle} />{children}</section>
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <label className="block text-sm font-medium text-gray-700">{label}{children}</label>
}

function ReviewItem({ label, value }: { label: string; value: string }) {
  return <div><h3 className="mb-2 font-semibold">{label}</h3><p className="whitespace-pre-wrap break-words text-gray-700">{value}</p></div>
}

function AttachmentUpload({ category, items, onAdd, onRemove }: {
  category: Attachment["category"]
  items: Attachment[]
  onAdd: (files: File[] | FileList | null, category: Attachment["category"]) => void
  onRemove: (id: number) => void
}) {
  const [emptySlots, setEmptySlots] = useState(1)
  const [error, setError] = useState("")
  const label = category === "arguments" ? "Argument documents" : "Evidence and other supporting documents"
  function select(file: File, replaceId?: number) {
    if (file.type !== "application/pdf" && !file.name.toLowerCase().endsWith(".pdf")) {
      setError("Please select a PDF file.")
      return
    }
    if (file.size > 20 * 1024 * 1024) {
      setError("The file must not exceed 20 MB.")
      return
    }
    setError("")
    if (replaceId !== undefined) onRemove(replaceId)
    else setEmptySlots(current => Math.max(1, current - 1))
    onAdd([file], category)
  }
  return (
    <div className="space-y-4">
      {items.map((item, index) => <FileUploadSlot key={item.id} label={`${label} ${index + 1}`} file={item.file} fileName={item.file.name} uploading={false} onSelect={file => select(file, item.id)} onDelete={() => onRemove(item.id)} />)}
      {Array.from({ length: emptySlots }, (_, index) => (
        <div key={`empty-${index}`} className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <FileUploadSlot label={index === 0 ? label : `${label} - additional file ${index + 1}`} uploading={false} onSelect={file => select(file)} onDelete={() => {}} />
          </div>
          {index > 0 && (
            <button
              type="button"
              aria-label={`Remove empty attachment slot ${index + 1}`}
              className="mt-7 shrink-0 rounded p-2 text-gray-500 hover:bg-gray-100 hover:text-red-600"
              onClick={() => setEmptySlots(current => Math.max(1, current - 1))}
            >
              <FiX aria-hidden="true" />
            </button>
          )}
        </div>
      ))}
      {error && <p role="alert" className="text-xs text-red-600">{error}</p>}
      <button type="button" className={secondaryButton} onClick={() => setEmptySlots(current => current + 1)}><FiPlus /> Add attachments</button>
    </div>
  )
}

function AttachmentList({ items, onRemove }: { items: Attachment[]; onRemove?: (id: number) => void }) {
  return items.length ? <ul className="mt-3 divide-y divide-gray-200">{items.map(item => <li key={item.id} className="flex items-center justify-between gap-3 py-3 text-sm">
    <div className="min-w-0"><AttachmentLink file={item.file} /><span className="mt-1 block text-xs text-gray-500">{(item.file.size / 1024).toFixed(0)} KB</span></div>
    {onRemove && <button type="button" aria-label={`Remove ${item.file.name}`} className="shrink-0 rounded p-2 text-red-600 hover:bg-red-50" onClick={() => onRemove(item.id)}><FiTrash2 /></button>}
  </li>)}</ul> : <p className="mt-3 text-xs text-gray-500">No files added.</p>
}

function AttachmentLink({ file }: { file: File }) {
  const [url, setUrl] = useState("")
  const isPdf = file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf")
  useEffect(() => {
    if (isPdf) return
    const objectUrl = URL.createObjectURL(file)
    setUrl(objectUrl)
    return () => URL.revokeObjectURL(objectUrl)
  }, [file, isPdf])
  return isPdf ? <PdfPreview file={file} /> : <a href={url || undefined} target="_blank" rel="noreferrer" download={!/^image\/(png|jpeg|webp|gif|bmp|avif)$/.test(file.type)} className="break-words text-dantzBlue underline">{file.name}</a>
}
